import { faUpDown, faArrowRightArrowLeft, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from "luxon";
import { ChangeEvent, FormEvent, use, useCallback, useContext, useEffect, useState } from 'react';
import { Button, Col, Collapse, Form, FormGroup, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Row as tRow } from "react-table";

import useAlert from '../../../Alerts/useAlert';
import CentredSpinner from '../../../Components/CentredSpinner';
import DateTimePicker from '../../../Components/DateTimePickerV2';
import DebouncedInput from '../../../Components/DebouncedInput';
import MultiselectBox from '../../../Components/MultiselectBox';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { UserPreferencesContext } from '../../../Contexts/UserPreferencesContext';
import CommentDialog from '../../../Dialogs/CommentDialog';
import ConfirmationDialog from '../../../Dialogs/ConfirmationDialog';
import { DraggableModalDialog } from '../../../Dialogs/DraggableModalDialog';
import { Quality } from '../../../Types/LotDetailQuality';
import { Package } from '../../../Types/Package';
import { convertToDateTime, useDateUtils } from "../../../Utilities/useDateUtils";
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import LotSplitPackageDialogTable from './LotSplitPackageDialogTable';
import { useSelectGradeDialog } from '../../../Dialogs/SelectGradeDialog';

export enum LotSplitTypes {
    TransferSplit,
    AutoSplit,
    Quality,
    Proxy,
    Create,
    Reassign,
    CreateMultiple,
    QualityMultiple,
    UndoTransferSplit,
    UndoPackageSplit
};

interface LotSplitPackageDialogProps {
    showDialog: boolean,
    closeDialog: () => void,
    alternativeGrades: string[],
    allGrades: string[],
    splitType?: LotSplitTypes,
    includeQualitiesSelection?: boolean,
    showSplitTimes?: boolean,
    readonlySplitTimes?: boolean
}

function LotSplitPackageDialog({ splitType = undefined, showDialog, closeDialog, alternativeGrades, allGrades, includeQualitiesSelection = false, showSplitTimes = true, readonlySplitTimes = false }: LotSplitPackageDialogProps) {
    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const { lotDetail: {
        summary: {
            businessUnitCode,
            lotNumber: lotANumber,
            grade: initialLotAGrade,
            buildStartUtc,
            buildEndUtc
        },
        qualities,
        packages: lotAPackages,
        qirs },
        getLotDetail,
        lotFlags: {
            splitTimeIsAFilter
        }
    } = useContext(LotDetailContext);

    const [loading, setLoading] = useState(false);

    const [lotBNumber, setLotBNumber] = useState("");
    const [lotBNumberExists, setLotBNumberExists] = useState(false);
    const [bLotManuallyEntered, setbLotManuallyEntered] = useState(false);
    const [lotAGrade, setLotAGrade] = useState(initialLotAGrade);
    const [lotAWeight, setLotAWeight] = useState(0);
    const [lotBGrade, setLotBGrade] = useState(initialLotAGrade);
    const [lotBWeight, setLotBWeight] = useState(0);
    const [lotBPackages, setLotBPackages] = useState<Package[]>([]);
    const [undoToken, setUndoToken] = useState(0);
    const [formValidated, setFormValidated] = useState(false);
    const [confirmation, setConfirmation] = useState(<></>);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);

    const { convertUTCToShortDateTime } = useDateUtils();
    const { show: showSelectGradeDialog, SelectGradeDialog } = useSelectGradeDialog();

    useEffect(() => {
        if (showDialog) {
            fetchDataAuthenticated(`api/v1/lot/${lotANumber}/getSuggestedSplitLotNumber`, "GET")
                .then(async (response: Response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then(data => {
                    if (!bLotManuallyEntered) {
                        setLotBNumber(data);
                    }
                });
        }
    }, [showDialog, bLotManuallyEntered, fetchDataAuthenticated, lotANumber]);

    const [splitStartUtc, setSplitStartUtc] = useState<DateTime | null>(buildStartUtc);
    const [splitEndUtc, setSplitEndUtc] = useState<DateTime | null>(buildEndUtc);
    useEffect(() => {
        setSplitStartUtc(buildStartUtc);
    }, [buildStartUtc]);
    useEffect(() => {
        setSplitEndUtc(buildEndUtc);
    }, [buildEndUtc]);

    const [selectedQualities, setSelectedQualities] = useState<Quality[]>([]);

    const [allPackages, setAllPackages] = useState([
        ...lotAPackages?.map(p => ({ ...p, lot: "A" })),
        ...lotBPackages?.map(p => ({ ...p, lot: "B" }))
    ].sort((a, b) => Date.parse(a.packageDateTimeUtc) - Date.parse(b.packageDateTimeUtc))
    );

    const [filteredPackagesNumbers, setFilteredPackagesNumbers] = useState<string[]>(allPackages.map(x => x.containerNumber));

    const setFilteredPackages = useCallback((rows: tRow<Package>[]) => {
        const packageNumbers = rows.map(r => r.original.containerNumber);
        if (filteredPackagesNumbers.length !== packageNumbers.length || !packageNumbers.every((x, index) => x === filteredPackagesNumbers[index])) {
            setFilteredPackagesNumbers(packageNumbers);
        }
    }, [filteredPackagesNumbers, setFilteredPackagesNumbers])

    useEffect(() => {
        setAllPackages([
            ...lotAPackages?.map(p => ({ ...p, lot: "A" })),
            ...lotBPackages?.map(p => ({ ...p, lot: "B" }))
        ].sort((a, b) => Date.parse(a.packageDateTimeUtc) - Date.parse(b.packageDateTimeUtc)));
    }, [lotAPackages, lotBPackages, undoToken]);

    useEffect(() => {
        const totalWeightA: number = allPackages.filter(p => p.lot === "A").reduce((accumulator, current) => accumulator + current.netWeight, 0) ?? 0;
        const totalWeightB: number = allPackages.filter(p => p.lot === "B").reduce((accumulator, current) => accumulator + current.netWeight, 0) ?? 0;

        setLotAWeight(totalWeightA);
        setLotBWeight(totalWeightB);

    }, [allPackages])

    const onChangeLotBGrade = useCallback((selectedGrade: string | null) => {

        if (selectedGrade !== null) {
            setLotBGrade(selectedGrade);
        }
    }, []);

    useEffect(() => {
        if (showDialog && lotBNumber && lotBNumber !== "") {
            fetchDataAuthenticated(`api/v1/lot/${lotBNumber}/exists`, "GET")
                .then(async (response: Response) => {
                    if (response.ok) {
                        return response.json();
                    }
                })
                .then(data => setLotBNumberExists(data));
        }
    }, [showDialog, lotBNumber, fetchDataAuthenticated]);

    useEffect(() => {
        if (lotBNumberExists === true && lotBNumber !== lotANumber) {
            fetchDataAuthenticated(`api/v1/lot/${lotBNumber}/splitDestinationDetails`, "GET")
                .then(async (response: Response) => {
                    if (response.ok) {
                        return response.json();
                    }
                    if (response.status === 404) {
                        return "";
                    }
                })
                .then(data => {
                    if (data === "") {
                        // Reset field values if we swap over to a new lot
                        setSelectedQualities([]);
                        setLotBGrade(initialLotAGrade);
                        setSplitStartUtc(buildStartUtc);
                        setSplitEndUtc(buildEndUtc);
                        setLotBPackages([]);
                        return;
                    }

                    setLotBGrade(data.grade);

                    setSplitStartUtc(DateTime.fromISO(data.lotStartUtc, { zone: "utc" }));
                    setSplitEndUtc(DateTime.fromISO(data.lotEndUtc, { zone: "utc" }));

                    if (data.proxyQualities) {
                        // Match up qualities that the destination is split for (based on the split records) with the 
                        // qualities available in the quality picker
                        var proxyQualities: Quality[] = [];
                        for (let i = 0; i < data.proxyQualities.length; i++) {
                            var quality = qualities.find((f: Quality) => f.analysis === data.proxyQualities[i].analysis && f.component === data.proxyQualities[i].component);

                            if (quality) {
                                proxyQualities = [...proxyQualities, quality];
                            }
                        }
                        setSelectedQualities(proxyQualities);
                    }

                    if (data.packages && data.packages.length !== 0) {
                        setLotBPackages(data.packages);
                    }
                });
        }
    }, [lotBNumber, lotBNumberExists, fetchDataAuthenticated, lotANumber, qualities]);

    const handleDateChange = useCallback((splitStartUtc: DateTime | null, splitEndUtc: DateTime | null) => {

        if (!splitTimeIsAFilter) {
            if (splitStartUtc == null || splitEndUtc == null || buildStartUtc == null || buildEndUtc == null ||
                (splitStartUtc.equals(buildStartUtc) && splitEndUtc.equals(buildEndUtc))) {
                // If you pick the source lots start & end times as the split end times, we ignore than and just select everything again on the source lot
                setAllPackages([
                    ...allPackages?.map(p => ({ ...p, lot: "A" })),
                ].sort((a, b) => Date.parse(a.packageDateTimeUtc) - Date.parse(b.packageDateTimeUtc)));
            }
            else {
                // Pick packages that will fall in the lot split window and move them to the B lot
                let packagesInSplit = allPackages.filter(p => convertToDateTime(p.packageDateTimeUtc)! >= splitStartUtc && convertToDateTime(p.packageDateTimeUtc)! <= splitEndUtc);
                let packagesNotInSplit = allPackages.filter(p => convertToDateTime(p.packageDateTimeUtc)! < splitStartUtc || convertToDateTime(p.packageDateTimeUtc)! > splitEndUtc);

                setAllPackages([
                    ...packagesNotInSplit?.map(p => ({ ...p, lot: "A" })),
                    ...packagesInSplit?.map(p => ({ ...p, lot: "B" }))
                ]);
            }
        }

        setSplitStartUtc(splitStartUtc);
        setSplitEndUtc(splitEndUtc);
    }, [allPackages, buildStartUtc, buildEndUtc]);

    const [formErrors, setFormErrors] = useState<{ [field: string]: string | undefined }>({});
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (includeQualitiesSelection && selectedQualities.length === 0) {
            setFormErrors((e) => ({ ...e, proxyQualities: "At least one quality needs to be selected" }));
            setFormValidated(false);
            return;
        }

        if (splitType === LotSplitTypes.Reassign && !lotBNumberExists) {
            setFormErrors((e) => ({ ...e, lotBNumber: "Reassignment can only occur between existing lots" }));
            setFormValidated(false);
            return;
        }

        if (form.checkValidity() === true) {
            if (allPackages?.filter(p => p.lot === "A").length === 0) {
                setConfirmation(<>
                    <p>This would result in lot {lotANumber} having no packages.</p>
                    <p>Are you sure you wish to delete lot {lotANumber}?</p>
                </>);
                setShowConfirmationDialog(true);
            } else if ([...new Set(allPackages?.filter(p => p.lot === "B").map(p => p.packageType))].length > 1) {
                setConfirmation(<>
                    <p>This would result in lot {lotBNumber} containing multiple package types.</p>
                    <p>Are you sure you want to continue?</p>
                </>);
                setShowConfirmationDialog(true);
            } else {
                setShowCommentDialog(true);
            }
        }

        setFormValidated(true);
    };

    // Clear form error when a lot is selected that does exist
    useEffect(() => {
        if (lotBNumberExists && !!formErrors.lotBNumber) {
            setFormErrors((e) => ({ ...e, lotBNumber: undefined }));
        }
    }, [formErrors?.lotBNumber, lotBNumberExists]);

    const splitLotWithComment = async (comment: string) => {
        let splitStartUtcString = showSplitTimes && !readonlySplitTimes && splitStartUtc !== null
            ? splitStartUtc.toISO({ includeOffset: false })
            : null;
        let splitEndUtcString = showSplitTimes && !readonlySplitTimes && splitEndUtc !== null
            ? splitEndUtc.toISO({ includeOffset: false })
            : null;

        var requestBody: any = {
            lotPackages: [
                {
                    lotNumber: lotANumber,
                    grade: lotAGrade,
                    packages: allPackages.filter(p => p.lot === "A")
                },
                {
                    lotNumber: lotBNumber,
                    grade: lotBGrade,
                    packages: allPackages.filter(p => p.lot === "B")
                },
            ],
            splitStartUtc: splitStartUtcString,
            splitEndUtc: splitEndUtcString,
            comment: comment
        };

        if (selectedQualities.length > 0) {
            requestBody.qualities = selectedQualities;
        }

        setLoading(true);
        await fetchDataAuthenticated(`api/v1/lot/${lotANumber}/splitpackages`, "POST", JSON.stringify(requestBody))
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", t("LotSplit.Successful"));
                    getLotDetail();
                    closeDialogAndResetForm();
                } else {
                    let errorMessage = await response.text();
                    throw new Error(`${t("LotSplit.Failed")} ${errorMessage}`);
                }
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    };

    const changePackageLot = (containerNumbers: string[], lotChecked: string) => {
        setAllPackages([...allPackages.map(p => containerNumbers.includes(p.containerNumber) ? { ...p, lot: lotChecked } : p)]);
    };

    const closeDialogAndResetForm = () => {
        setLoading(false);
        setbLotManuallyEntered(false)
        setAllPackages([...allPackages?.map(p => ({ ...p, lot: "A" }))]);
        closeDialog();
    };

    const selectAllPackages = useCallback((lot: string) => {
        if (lot === "A") {
            setAllPackages(allPackages => [...allPackages?.map(p => ({ ...p, lot: filteredPackagesNumbers.includes(p.containerNumber) ? "A" : p.lot }))]);
        }
        if (lot === "B") {
            setAllPackages(allPackages => [...allPackages?.map(p => ({ ...p, lot: filteredPackagesNumbers.includes(p.containerNumber) ? "B" : p.lot }))]);
        }
    }, [filteredPackagesNumbers])

    const { userPreferences, saveUserPreference } = use(UserPreferencesContext);
    const [showHelpText, setShowHelpText] = useState(false);
    var lotsAreTheSame = lotANumber === lotBNumber;

    useEffect(() => {
        setShowHelpText(!!userPreferences?.lotDetail?.showLotSplitHelpText);
    }, [userPreferences?.lotDetail?.showLotSplitHelpText]);

    const toggleShowHelpText = () => {
        const newValue = !showHelpText;
        setShowHelpText(newValue);
        saveUserPreference("lotDetail.showLotSplitHelpText", newValue);
    };

    return (
        showDialog &&
        <>
            <Modal dialogAs={DraggableModalDialog} show={showDialog} onHide={closeDialog} onExit={closeDialog} backdrop="static" size="lg" className="verywide-dialog tall-dialog">
                <Modal.Header closeButton>
                    <div className="d-flex w-100">
                        <Modal.Title>
                            {splitType ? t<string>(`LotSplit.AltTitles.${LotSplitTypes[splitType]}`, { lotNumber: lotANumber }) : "Lot Split"}
                        </Modal.Title>
                        {splitType &&
                            <Button variant={"outline-primary"} className="rounded-circle p-1 ms-auto me-3" onClick={toggleShowHelpText}>
                                <FontAwesomeIcon size="2xl" icon={faCircleInfo} />
                            </Button>
                        }
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {loading
                        ? <CentredSpinner />
                        : <>
                            {splitType &&
                                <Collapse in={showHelpText}>
                                    <div id="package-split-help-text">
                                        {((t(`LotSplit.HelpText.${LotSplitTypes[splitType]}`, { returnObjects: true }) as string[]) ?? [])
                                            .map((t, index) => <div key={`package-split-help-text-${index}`}>{t}</div>)
                                        }
                                        <hr />
                                    </div>
                                </Collapse>
                            }
                            <Form id="lotSplitByPackageForm" noValidate validated={formValidated} onSubmit={handleSubmit}>
                                <FormGroup as={Row} className="mb-3">
                                    <Col>
                                        <Form.Label>{t<string>("LotSplit.SplitFrom")}</Form.Label>
                                        <Form.Control required readOnly type="text" value={lotANumber} />
                                        <Row className="mt-2">
                                            <Col sm={3} className="py-2">
                                                <Form.Label>{t<string>("LotSplit.Grade")}:</Form.Label>
                                            </Col>
                                            <Col sm={9}>
                                                <Form.Select
                                                    aria-label="Select grade"
                                                    name="grade"
                                                    required
                                                    value={lotAGrade}
                                                    disabled={lotsAreTheSame}
                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setLotAGrade(e.currentTarget.value)}>
                                                    <option value="">{t<string>("Application.Controls.DefaultSelect")}</option>
                                                    {!(alternativeGrades.includes(lotAGrade)) && <option value={lotAGrade}>{lotAGrade}</option>}
                                                    {alternativeGrades.map((grade, index) =>
                                                        <option key={`altgrade-option-${index}`} value={grade}>{grade}</option>
                                                    )}
                                                    <option value="" disabled>------------</option>
                                                    {allGrades.map((grade, index) =>
                                                        <option key={`allgrade-option-${alternativeGrades.length + 1 + index}`} value={grade}>{grade}</option>
                                                    )}
                                                </Form.Select>
                                            </Col>
                                        </Row>
                                        <Row className="mt-2">
                                            <Col sm={3} className="py-2">
                                                <Form.Label>{t<string>("LotSplit.TotalWeight")}:</Form.Label>
                                            </Col>
                                            <Col sm={9}>
                                                <Form.Control required readOnly type="text" value={lotAWeight} />
                                            </Col>
                                        </Row>
                                        {includeQualitiesSelection &&
                                            <MultiselectBox<Quality>
                                                id={"proxyQualities"}
                                                name={t("LotSplit.Qualities")}
                                                options={qualities}
                                                selectedOptions={selectedQualities}
                                                setSelectedOptions={setSelectedQualities}
                                                map="name"
                                                errors={formErrors} />
                                        }
                                    </Col>
                                    <Col sm={1} className="mt-4 py-2">
                                        <FontAwesomeIcon icon={faArrowRightArrowLeft} size={"lg"} />
                                    </Col>
                                    <Col>
                                        <Form.Group as={Row}>
                                            <Form.Label>{t<string>("LotSplit.SplitTo")}</Form.Label>
                                            <DebouncedInput required type="text" value={lotBNumber} onChange={(value) => setLotBNumber(value)} immediateAction={() => setbLotManuallyEntered(true)} isInvalid={!!formErrors.lotBNumber} />
                                            <Form.Control.Feedback type="invalid">{formErrors.lotBNumber}</Form.Control.Feedback>
                                        </Form.Group>
                                        <Row className="mt-2">
                                            <Col sm={3} className="py-2">
                                                <Form.Label>{t<string>("LotSplit.Grade")}:</Form.Label>
                                            </Col>
                                            <Col sm="auto">
                                                <Form.Select
                                                    aria-label="Select grade"
                                                    name="grade"
                                                    required
                                                    value={lotBGrade}
                                                    disabled={lotsAreTheSame}
                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setLotBGrade(e.currentTarget.value)}>
                                                    <option value="">{t<string>("Application.Controls.DefaultSelect")}</option>
                                                    {!(alternativeGrades.includes(lotAGrade)) && <option value={lotAGrade}>{lotAGrade}</option>}
                                                    {alternativeGrades.map((grade, index) =>
                                                        <option key={`altgrade-option-${index}`} value={grade}>{grade}</option>
                                                    )}
                                                    <option value="" disabled>------------</option>
                                                    {allGrades.map((grade, index) =>
                                                        <option key={`allgrade-option-${alternativeGrades.length + 1 + index}`} value={grade}>{grade}</option>
                                                    )}
                                                </Form.Select>

                                            </Col>
                                            <Col sm="auto" className="ps-0">
                                                <Button className="ms-2"
                                                    onClick={() => {
                                                        console.log(`Split splitStartUtc=${splitStartUtc}, splitEndUtc=${splitEndUtc}`);
                                                        showSelectGradeDialog();
                                                    }}>
                                                    <FontAwesomeIcon icon={faUpDown} />
                                                </Button>
                                            </Col>
                                        </Row>
                                        <Row className="mt-2">
                                            <Col sm={3} className="py-2">
                                                <Form.Label>{t<string>("LotSplit.TotalWeight")}:</Form.Label>
                                            </Col>
                                            <Col sm={9}>
                                                <Form.Control required readOnly type="text" value={lotBWeight} />
                                            </Col>
                                        </Row>
                                        {showSplitTimes &&
                                            <Row className="mt-2">
                                                <Col sm={3} className="py-2">
                                                    <Form.Label>{t<string>("LotSplit.TimeRange")}:</Form.Label>
                                                </Col>
                                                <Col sm={4}>
                                                    {readonlySplitTimes
                                                        ? <Form.Control plaintext readOnly value={`${convertUTCToShortDateTime(splitStartUtc)}`} />
                                                        : <DateTimePicker name="splitStart" dateTimeUTC={splitStartUtc} handleDateSelect={(date) => handleDateChange(date, splitEndUtc)} isClearable={true} required={false} />
                                                    }
                                                </Col>
                                                <Col sm={4}>
                                                    {readonlySplitTimes
                                                        ? <Form.Control plaintext readOnly value={`${convertUTCToShortDateTime(splitEndUtc)}`} />
                                                        : <DateTimePicker name="splitEnd" dateTimeUTC={splitEndUtc} handleDateSelect={(date) => handleDateChange(splitStartUtc, date)} isClearable={true} required={false} />
                                                    }
                                                </Col>
                                            </Row>
                                        }
                                    </Col>
                                </FormGroup>
                                <FormGroup>
                                    <LotSplitPackageDialogTable
                                        firstLotNumber={lotANumber}
                                        secondLotNumber={lotBNumber}
                                        packages={allPackages}
                                        qirs={qirs}
                                        filteredPackagesNumbers={filteredPackagesNumbers}
                                        setFilteredPackages={setFilteredPackages}
                                        changePackageLot={changePackageLot}
                                        selectAllPackages={selectAllPackages}
                                        splitTimeIsAFilter={splitTimeIsAFilter}
                                        fromDateTime={splitStartUtc}
                                        toDateTime={splitEndUtc}
                                    />
                                </FormGroup>
                            </Form>
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Col>
                        <Button disabled={lotsAreTheSame} onClick={() => setUndoToken(t => t + 1)}>
                            {t<string>("Application.Controls.Undo")}
                        </Button>
                    </Col>
                    <Col className="d-flex justify-content-end">
                        <Button disabled={lotsAreTheSame} variant="secondary" onClick={closeDialogAndResetForm} className="me-2">
                            {t<string>("Application.Controls.Cancel")}
                        </Button>
                        <Button disabled={lotsAreTheSame} variant="primary" type="submit" form="lotSplitByPackageForm" name="confirmSplit">
                            {t<string>("Application.Controls.Ok")}
                        </Button>
                    </Col>
                </Modal.Footer>
            </Modal>
            <ConfirmationDialog showDialog={showConfirmationDialog} closeDialog={() => setShowConfirmationDialog(false)} onOk={() => setShowCommentDialog(true)}>
                {confirmation}
            </ConfirmationDialog>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={splitLotWithComment} />
            <SelectGradeDialog businessUnitCode={businessUnitCode} lotNumber={lotANumber} onClose={onChangeLotBGrade} splitStartUtc={splitStartUtc} splitEndUtc={splitEndUtc} qualities={selectedQualities} />
        </>
    );
}

export default LotSplitPackageDialog;